import { useParams } from "react-router-dom";
import { Section } from "./components/Section/Section";
import { StyledResume } from "./styles";

export const Resume = () => {
  const { locale } = useParams();
  const content = locale === "es" ? spanishContent : englishContent;

  return (
    <StyledResume>
      <div className="header">
        <div>
          <h1 className="name">Yair Velasco</h1>
          <h2 className="position">Senior Software Engineer | React, TypeScript, Node.js</h2>
        </div>
        <div>
          <div>
            <b>{content.phoneLabel}:</b> +52 8116555523
          </div>
          <div>
            <b>{content.emailLabel}:</b> yair_vnp@hotmail.com
          </div>
        </div>
      </div>
      <hr></hr>
      <Section title={content.professionalSummaryTitle}>
        <p>{content.professionalSummary}</p>
      </Section>

      <Section title={content.workExperienceTitle}>
        {workExperience.map((experience, index) => (
          <div key={index} className="work-experience">
            <div className="company-name">{experience.companyName}</div>
            <div className="position">
              {experience.position}
              <span>{experience.dates[content.language]}</span>
            </div>
            <div>{experience.description[content.language]}</div>
            <ul>
              {experience.keyRoles[content.language].map((keyRole, index) => (
                <li key={index}>{keyRole}</li>
              ))}
            </ul>
            <div className="technologies">
              <span>{content.technologies}: </span>
              {experience.techList}
            </div>
          </div>
        ))}
      </Section>
      <Section title={content.technicalSkillsTitle}>
        {technicalSkills.map((skill, index) => (
          <div key={index} className="tech-skill">
            <div className="title">{skill.title[content.language]}</div>
            <div className="list">{skill.list}</div>
          </div>
        ))}
      </Section>
      <Section title={content.educationTitle}>
        <div className="education">
          <span className="program-name">
            {content.educationProgram},
          </span>{" "}
          {content.educationInstitution}
        </div>
      </Section>
    </StyledResume>
  );
};

const workExperience = [
  {
    companyName: "LoanLight, Inc",
    position: "Senior Fullstack Engineer",
    dates: { en: "Dec 2025 - Jun 2026", es: "Dic 2025 - Jun 2026" },
    description: {
      en: "LoanLight builds software that uses AI to automate operational checks in underwriting, giving underwriters more time for risk assessment and decision-making.",
      es: "LoanLight desarrolla software que utiliza IA para automatizar verificaciones operativas del proceso de underwriting y permitir que los underwriters se enfoquen en evaluar riesgos y tomar decisiones.",
    },
    keyRoles: {
      en: [
        "Owned the application's frontend architecture.",
        "Led the migration from Lit web components to Next.js.",
        "Built document-viewing experiences for AI-driven agents.",
        "Maintained LangGraph nodes for data extraction, supporting AI-assisted document processing.",
        "Created a development workflow for faster AI-assisted feature delivery.",
      ],
      es: [
        "Estuve a cargo de la arquitectura frontend de la aplicación.",
        "Dirigí la migración de Lit web components a Next.js.",
        "Desarrollé experiencias para visualizar documentos desde agentes de IA.",
        "Mantuve nodos de LangGraph para la extracción de datos, como parte del procesamiento documental asistido por IA.",
        "Creé un flujo de desarrollo para entregar funcionalidades con mayor rapidez mediante herramientas de IA.",
      ],
    },
    techList: "Next.js, React, TypeScript, Lit, AI-assisted workflows",
  },
  {
    companyName: "Skolify",
    position: "Founder",
    dates: { en: "Apr 2025 - Present", es: "Abr 2025 - Presente" },
    description: {
      en: "Education platform with a school administration web app and a mobile app for families and tutors.",
      es: "Plataforma educativa con una aplicación web para administración escolar y una app móvil para familias y tutores.",
    },
    keyRoles: {
      en: [
        "Built the mobile app and admin web app, integrating Expo/React Native and Next.js with Firebase services.",
        "Developed Cloud Functions and strengthened access controls with Firestore Security Rules and App Check.",
        "Contributed to chat, notifications, and Stripe billing.",
        "Led user acceptance testing and mobile releases to Google Play and the App Store.",
      ],
      es: [
        "Desarrollé la app móvil y el panel web administrativo, integrando Expo/React Native y Next.js con servicios de Firebase.",
        "Desarrollé Cloud Functions y reforcé el control de acceso con Firestore Security Rules y App Check.",
        "Contribuí a las funciones de chat y notificaciones, y a la facturación con Stripe.",
        "Coordiné las pruebas de aceptación (UAT) y los lanzamientos móviles en Google Play y App Store.",
      ],
    },
    techList: "Expo / React Native, Next.js, Firebase (Auth, Cloud Functions, Firestore), Firestore Security Rules, App Check, Stripe, Zod, Jest, Playwright, Firebase Emulator Suite",
  },
  {
    companyName: "Regrow",
    position: "Senior Software Engineer",
    dates: { en: "Sep 2022 - Mar 2025", es: "Sep 2022 - Mar 2025" },
    description: {
      en: "Regrow develops software for agricultural resilience. Contributed to Sustainability Insights, building reporting and comparison workflows for complex field data.",
      es: "Regrow desarrolla software para la resiliencia agrícola. Contribuí a Sustainability Insights con flujos de reportes y comparación para datos complejos de parcelas.",
    },
    keyRoles: {
      en: [
        "Created the Sustainability Reporting dashboard, reducing report generation from days to minutes.",
        "Built the Supply Shed comparison feature to streamline field comparisons.",
        "Planned and refactored the migration from Redux to SWR for API data.",
        "Raised unit test coverage for calculation functions from 40% to 85%.",
        "Implemented Playwright E2E tests covering 100% of critical user flows.",
        "Contributed MUI components to Regrow's Leaf Design System.",
      ],
      es: [
        "Creé el dashboard de Sustainability Reporting y reduje la generación de reportes de días a minutos.",
        "Desarrollé la funcionalidad Supply Shed comparison para agilizar la comparación de parcelas.",
        "Planifiqué y refactoricé la migración de Redux a SWR para los datos de API.",
        "Aumenté la cobertura de pruebas unitarias de funciones de cálculo del 40 % al 85 %.",
        "Implementé pruebas E2E con Playwright para cubrir el 100% de los flujos críticos.",
        "Contribuí componentes de MUI al Leaf Design System de Regrow.",
      ],
    },
    techList: "React, TypeScript, styled-components, Redux, SWR, MUI, Playwright",
  },
  {
    companyName: "Flote",
    position: "Lead Software Engineer",
    dates: { en: "Aug 2021 - Oct 2022", es: "Ago 2021 - Oct 2022" },
    description: {
      en: "Censorship-resistant social network for real-time messaging, notifications, and livestreaming.",
      es: "Red social resistente a la censura, con mensajería, notificaciones y transmisiones en vivo en tiempo real.",
    },
    keyRoles: {
      en: [
        "Implemented and refactored NestJS backend features, aligning GraphQL schemas and API contracts with product needs.",
        "Built real-time notifications and messaging with WebSockets and Redis.",
        "Led software delivery, established engineering practices, and mentored developers on the frontend team.",
      ],
      es: [
        "Implementé y refactoricé funcionalidades backend en NestJS, alineando schemas de GraphQL y contratos de API con las necesidades del producto.",
        "Desarrollé notificaciones y mensajería en tiempo real con WebSockets y Redis.",
        "Lideré la entrega de software, establecí prácticas de ingeniería y di mentoría a desarrolladores del equipo frontend.",
      ],
    },
    techList: "React, TypeScript, Node.js, NestJS, GraphQL, WebSockets, Redis, MobX, Apollo, styled-components, Quill",
  },
  {
    companyName: "AgileThought",
    position: "Senior Software Engineer",
    dates: { en: "Aug 2020 - Aug 2021", es: "Ago 2020 - Ago 2021" },
    description: {
      en: "Contributed to a funding-management application for Kirkland & Ellis, refactoring legacy code and delivering features through release.",
      es: "Contribuí a una aplicación de administración de fondos para Kirkland & Ellis, refactorizando código existente y llevando funcionalidades hasta su lanzamiento.",
    },
    keyRoles: {
      en: [
        "Delivered features from planning through release.",
        "Refactored legacy code to align with the project's current technologies and practices.",
        "Contributed to technical decisions on data-related challenges.",
      ],
      es: [
        "Desarrollé funcionalidades desde la planeación hasta el lanzamiento.",
        "Refactoricé código existente para alinearlo con la tecnología y las prácticas actuales del proyecto.",
        "Participé en decisiones técnicas para resolver problemas relacionados con datos.",
      ],
    },
    techList: "React, TypeScript, MobX, Sass modules",
  },
  {
    companyName: "IBM",
    position: "Software Engineer",
    dates: { en: "Oct 2017 - Aug 2020", es: "Oct 2017 - Ago 2020" },
    description: {
      en: "Led a five-person team building a contract-management solution used by nearly 1,000 IBM procurement professionals to manage contracts, identify risks, and link them to purchase orders.",
      es: "Lideré un equipo de cinco personas en el desarrollo de una solución de gestión de contratos utilizada por cerca de 1,000 profesionales de procurement en IBM para administrar contratos, detectar riesgos y vincularlos con órdenes de compra.",
    },
    keyRoles: {
      en: [
        "Built backend APIs with Node.js and Express.",
        "Set up IBM Cloud infrastructure for CI/CD, application monitoring, and the app's cloud services.",
        "Led the migration from jQuery to React to improve maintainability and accelerate delivery.",
        "Designed UX flows and interfaces for core and advanced features.",
        "Followed up on implementation to keep delivered features aligned with UX goals.",
        "Facilitated a Design Thinking workshop in Raleigh with 25+ participants to shape the product roadmap.",
      ],
      es: [
        "Construí APIs backend con Node.js y Express.",
        "Configuré la infraestructura de IBM Cloud para CI/CD, el monitoreo de la aplicación y sus servicios en la nube.",
        "Lideré la migración de jQuery a React para mejorar la mantenibilidad y acelerar las entregas.",
        "Diseñé flujos UX e interfaces para funcionalidades básicas y avanzadas.",
        "Di seguimiento a la implementación para mantener las funcionalidades alineadas con los objetivos de UX.",
        "Facilité un taller de Design Thinking en Raleigh con más de 25 participantes para definir el roadmap del producto.",
      ],
    },
    techList: "React, UX, UI, jQuery, Node.js, Express",
  },
];

const englishContent = {
  language: "en" as const,
  professionalSummaryTitle: "PROFESSIONAL SUMMARY",
  professionalSummary: "Senior Software Engineer with 8+ years building production software across web, mobile, APIs, and cloud services. Hands-on experience spanning React/TypeScript interfaces, Node.js services, real-time workflows, data validation, access control, and AI-assisted document processing. Delivers end-to-end features—from API contracts and backend logic to customer-facing experiences—in close collaboration with product and engineering teams.",
  workExperienceTitle: "WORK EXPERIENCE",
  technicalSkillsTitle: "TECHNICAL SKILLS",
  educationTitle: "EDUCATION",
  educationProgram: "B.S. in Animation and Digital Art",
  educationInstitution: "Monterrey Institute of Technology and Higher Education (ITESM), Mexico, 2015",
  technologies: "Technologies",
  phoneLabel: "Phone",
  emailLabel: "Email",
};

const spanishContent = {
  ...englishContent,
  language: "es" as const,
  professionalSummaryTitle: "RESUMEN PROFESIONAL",
  professionalSummary: "Ingeniero de software senior con más de 8 años desarrollando software para web, móvil, APIs y servicios en la nube. Experiencia práctica desde interfaces con React/TypeScript hasta servicios con Node.js, flujos en tiempo real, validación de datos, control de acceso y procesamiento documental asistido por IA. Desarrollo funcionalidades de extremo a extremo —desde contratos de API y lógica backend hasta interfaces para usuarios— en colaboración con equipos de producto e ingeniería.",
  workExperienceTitle: "EXPERIENCIA LABORAL",
  technicalSkillsTitle: "HABILIDADES TÉCNICAS",
  educationTitle: "EDUCACIÓN",
  educationProgram: "Licenciatura en Animación y Arte Digital",
  educationInstitution: "Tecnológico de Monterrey (ITESM), México, 2015",
  technologies: "Tecnologías",
  phoneLabel: "Teléfono",
  emailLabel: "Correo",
};

const technicalSkills = [
  {
    title: { en: "Backend & Data:", es: "Backend y Datos:" },
    list: "Node.js, Express, NestJS, Firebase (Cloud Functions, Firestore, Auth), GraphQL, Redis, Cloudant (MongoDB), Zod",
  },
  {
    title: { en: "Frontend & Mobile:", es: "Frontend y Móvil:" },
    list: "React, React Native, Next.js, Apollo Client, MobX, Redux, styled-components",
  },
  {
    title: { en: "Languages & Testing:", es: "Lenguajes y Pruebas:" },
    list: "JavaScript, TypeScript, SQL, Jest, Vitest, Playwright, Firebase Emulator Suite, Git",
  },
];
